import { useEffect, useState, useCallback } from 'react';
import { automationApi, type AutomationDashboard } from '@/lib/adminApi';
import { toast, Toaster } from 'sonner';
import { Card, CardHeader, CardBody } from '@/components/ui/card';
import { StatCard, PageHeader, Skeleton } from '@/components/ui/page';
import { Button } from '@/components/ui/button';
import { RefreshCw, MessageCircle, TrendingUp, Package, RotateCcw, BarChart3 } from 'lucide-react';

const stageLabels: Record<string, string> = {
  new: 'New', pending_confirmation: 'Pending', confirmed: 'Confirmed',
  collecting_data: 'Collecting Data', data_completed: 'Data Ready',
  offering_upsell: 'Upselling', upsell_completed: 'Upsold',
  ready_for_shipping: 'Ready to Ship', shipped: 'Shipped',
  delivered: 'Delivered', returned: 'Returned', cancelled: 'Cancelled',
};
const stageColors: Record<string, string> = {
  new: 'bg-slate-500', pending_confirmation: 'bg-amber-500', confirmed: 'bg-emerald-500',
  collecting_data: 'bg-blue-500', data_completed: 'bg-cyan-500',
  offering_upsell: 'bg-purple-500', upsell_completed: 'bg-violet-500',
  ready_for_shipping: 'bg-indigo-500', shipped: 'bg-sky-500',
  delivered: 'bg-emerald-600', returned: 'bg-red-500', cancelled: 'bg-zinc-600',
};

export default function AutomationDashboardPage() {
  const [data, setData] = useState<AutomationDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setError('');
      const res = await automationApi.dashboard();
      if (res.success) setData(res.data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-4">
        <PageHeader title="WhatsApp Automation" description="Order lifecycle automation overview" />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <Toaster position="top-center" richColors />
      <PageHeader
        title="WhatsApp Automation"
        description="Order lifecycle automation overview"
        action={<Button size="sm" variant="outline" onClick={load}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>}
      />

      {error && (
        <div className="border border-red-500/30 bg-red-500/10 text-red-300 rounded-xl p-4 text-sm">{error}</div>
      )}

      {data && (
        <>
          {/* Order Pipeline */}
          <Card>
            <CardHeader title="Order Pipeline" description={`${data.orders.total} total orders`} />
            <CardBody>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stageLabels).map(([key, label]) => {
                  const count = data.orders.byStage[key] || 0;
                  if (count === 0 && !['new', 'pending_confirmation', 'confirmed'].includes(key)) return null;
                  return (
                    <div key={key} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-elevated border border-white/[0.04]">
                      <span className={`w-2 h-2 rounded-full ${stageColors[key] || 'bg-slate-500'}`} />
                      <span className="text-xs text-zinc-400">{label}</span>
                      <span className="text-sm font-semibold">{count}</span>
                    </div>
                  );
                })}
              </div>
            </CardBody>
          </Card>

          {/* KPI Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <StatCard label="Total Orders" value={data.orders.total} />
            <StatCard label="Upsell Revenue" value={`EGP ${data.upsells.revenue.toLocaleString()}`} />
            <StatCard label="Upsell Rate" value={`${data.upsells.acceptanceRate}%`} hint={`${data.upsells.accepted}/${data.upsells.offered}`} />
            <StatCard label="Ship Success" value={`${data.shipping.successRate}%`} hint={`${data.shipping.delivered}/${data.shipping.total}`} />
            <StatCard label="Recovery Rate" value={`${data.recovery.recoveryRate}%`} hint={`${data.recovery.requested}/${data.recovery.total}`} />
            <StatCard label="Reship Rate" value={`${data.recovery.reshipmentRate}%`} />
            <StatCard label="Customers" value={data.customers.total} />
          </div>

          {/* Upsells + Shipping + Recovery */}
          <div className="grid lg:grid-cols-3 gap-4">
            <Card>
              <CardHeader title="Upsells" icon={<TrendingUp className="h-4 w-4 text-purple-400" />} />
              <CardBody>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-zinc-400">Offered</span><span>{data.upsells.offered}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-400">Accepted</span><span className="text-emerald-400">{data.upsells.accepted}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-400">Rejected</span><span className="text-red-400">{data.upsells.rejected}</span></div>
                  <div className="flex justify-between border-t border-white/[0.06] pt-2"><span className="font-medium">Revenue</span><span className="font-medium text-emerald-400">EGP {data.upsells.revenue.toLocaleString()}</span></div>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Shipping" icon={<Package className="h-4 w-4 text-sky-400" />} />
              <CardBody>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-zinc-400">Total Shipments</span><span>{data.shipping.total}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-400">Delivered</span><span className="text-emerald-400">{data.shipping.delivered}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-400">Returned</span><span className="text-red-400">{data.shipping.returned}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-400">Failed</span><span className="text-amber-400">{data.shipping.failed}</span></div>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Recovery" icon={<RotateCcw className="h-4 w-4 text-amber-400" />} />
              <CardBody>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-zinc-400">Returned Orders</span><span>{data.recovery.total}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-400">Requested Reship</span><span className="text-emerald-400">{data.recovery.requested}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-400">Declined</span><span className="text-red-400">{data.recovery.declined}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-400">Reshipped</span><span className="text-sky-400">{data.recovery.reshipped}</span></div>
                </div>
              </CardBody>
            </Card>
          </div>

          {/* Customer Segments */}
          <Card>
            <CardHeader title="Customer Segments" description={`${data.customers.total} total customers`} />
            <CardBody>
              <div className="flex flex-wrap gap-2">
                {Object.entries(data.customers.segments).map(([seg, count]) => (
                  <div key={seg} className="px-3 py-1.5 rounded-lg bg-elevated border border-white/[0.04] text-sm">
                    <span className="text-zinc-400 capitalize">{seg.replace(/_/g, ' ')}</span>
                    <span className="ml-2 font-semibold">{count}</span>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}
